import type { ChatMessage, LobbyState } from './lobbyStore.types';
import { pruneMessages } from './prune-messages';

/** Maximum messages to keep per chat room (matches lobbyStore.chat.ts) */
const MAX_MESSAGES_PER_ROOM = 200;

/**
 * Cap on queued-but-unrevealed messages per room. A wedged reveal (a live
 * stream whose NpcStreamEnd never lands keeps `revealingMessageIds` pinned,
 * and the drain watchdog is disarmed while the head streams) would otherwise
 * grow the queue unboundedly: the same class of leak the game store capped
 * for BUG-6. Overflow is flushed straight into the transcript as finished
 * messages rather than dropped, so nothing a player never saw is lost.
 */
const MAX_REVEAL_QUEUE_PER_ROOM = 50;

export const revealQueueSlice: Pick<
  LobbyState,
  | 'revealQueues'
  | 'revealingMessageIds'
  | 'enqueueRevealMessage'
  | 'advanceRevealQueue'
  | 'finishReveal'
  | 'skipRevealQueue'
> = {
  revealQueues: {},
  revealingMessageIds: {},

  enqueueRevealMessage: (roomId: string, message: ChatMessage) => (state: LobbyState) => {
    const queue = [...(state.revealQueues[roomId] ?? []), message];
    if (queue.length <= MAX_REVEAL_QUEUE_PER_ROOM) {
      return {
        revealQueues: { ...state.revealQueues, [roomId]: queue },
      };
    }
    // Over cap: flush the oldest overflow into the transcript as finished
    // messages (see MAX_REVEAL_QUEUE_PER_ROOM). Queue order is preserved:
    // everything flushed precedes everything still queued.
    const overflow = queue.length - MAX_REVEAL_QUEUE_PER_ROOM;
    const flushed = queue.slice(0, overflow).map((m) => ({
      ...m,
      isStreaming: false,
      isQueuedReveal: false,
    }));
    const isActive = state.activeChatRoomId === roomId;
    const pinnedId = state.revealingMessageIds[roomId];
    return {
      revealQueues: { ...state.revealQueues, [roomId]: queue.slice(overflow) },
      chatRooms: state.chatRooms.map((r) => {
        if (r.id !== roomId) return r;
        return {
          ...r,
          messages: pruneMessages([...r.messages, ...flushed], MAX_MESSAGES_PER_ROOM, pinnedId),
          unreadCount: isActive ? 0 : r.unreadCount + flushed.length,
        };
      }),
    };
  },

  advanceRevealQueue: (roomId: string) => (state: LobbyState) => {
    const queue = state.revealQueues[roomId];
    if (!queue || queue.length === 0) return {};
    const [next, ...rest] = queue;
    // Canned reveal: `isStreaming` stays false so ProgressiveReveal's
    // catch-up branch (`stillStreaming` check) fires `onComplete`. Live WS
    // streams set `isStreaming: true` at their own write site (npc-events.ts
    // NpcStreamStart) and clear it via NpcStreamEnd or `finishReveal`.
    const revealed: ChatMessage = { ...next, isQueuedReveal: true };

    return {
      revealQueues: { ...state.revealQueues, [roomId]: rest },
      revealingMessageIds: { ...state.revealingMessageIds, [roomId]: next.id },
      chatRooms: state.chatRooms.map((r) => {
        if (r.id !== roomId) return r;
        const isActive = state.activeChatRoomId === roomId;
        return {
          ...r,
          messages: pruneMessages([...r.messages, revealed], MAX_MESSAGES_PER_ROOM, next.id),
          unreadCount: isActive ? 0 : r.unreadCount + 1,
        };
      }),
    };
  },

  finishReveal: (roomId: string, messageId: string) => (state: LobbyState) => ({
    chatRooms: state.chatRooms.map((r) =>
      r.id === roomId
        ? {
            ...r,
            messages: r.messages.map((m) =>
              m.id === messageId
                ? { ...m, isStreaming: false, isQueuedReveal: false, isLiveStream: false }
                : m
            ),
          }
        : r
    ),
    revealingMessageIds: {
      ...state.revealingMessageIds,
      [roomId]:
        state.revealingMessageIds[roomId] === messageId ? null : state.revealingMessageIds[roomId],
    },
  }),

  skipRevealQueue: (roomId: string) => (state: LobbyState) => {
    const currentRevealId = state.revealingMessageIds[roomId];
    const remaining = state.revealQueues[roomId] ?? [];
    if (!currentRevealId && remaining.length === 0) return {};
    const isActive = state.activeChatRoomId === roomId;
    // Defense-in-depth: callers gate destructive ops on `isLiveStream` at
    // the call site (LobbyRoomContent's Escape handler), but if a future
    // caller forgets, the store itself MUST NOT truncate a live WS stream
    // mid-flight — chunks still in flight would split into a new bubble.
    const room = state.chatRooms.find((r) => r.id === roomId);
    const currentRevealMsg = currentRevealId
      ? room?.messages.find((m) => m.id === currentRevealId)
      : undefined;
    const currentIsLive = currentRevealMsg?.isLiveStream === true;

    return {
      chatRooms: state.chatRooms.map((r) => {
        if (r.id !== roomId) return r;
        // Finish the currently revealing message UNLESS it's a live WS stream.
        const finishedMessages = r.messages.map((m) =>
          m.id === currentRevealId && !currentIsLive
            ? { ...m, isStreaming: false, isQueuedReveal: false }
            : m
        );
        // Dump all remaining queued messages at once
        const dumped = remaining.map((m) => ({
          ...m,
          isStreaming: false,
          isQueuedReveal: false,
        }));
        return {
          ...r,
          messages: pruneMessages(
            [...finishedMessages, ...dumped],
            MAX_MESSAGES_PER_ROOM,
            currentIsLive ? currentRevealId : null
          ),
          unreadCount: isActive ? 0 : r.unreadCount + dumped.length,
        };
      }),
      revealQueues: { ...state.revealQueues, [roomId]: [] },
      // Keep `revealingMessageIds[roomId]` registered when a live stream is
      // in flight so chunks keep landing in the same bubble; only clear it
      // for canned reveals.
      revealingMessageIds: currentIsLive
        ? state.revealingMessageIds
        : { ...state.revealingMessageIds, [roomId]: null },
    };
  },
};
